import { EVENTS, publish } from 'ia-util-pubsub'

class IaQtyPicker extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.js-qty-picker')
    this.plusButton = this.querySelector('.js-plus')
    this.minusButton = this.querySelector('.js-minus')
    // IaDrawer moves itself to <body> on connect, reconnecting every descendant;
    // an unguarded rebind here stacks click listeners and a tap counts twice
    if (this.dataset.iaBound) return
    this.dataset.iaBound = '1'
    this.setupEventListeners()
    this.syncButtons()
  }

  static get observedAttributes() {
    return ['value', 'max']
  }

  // Every value write lands here; ia-bundle.js also rewrites `max` at runtime.
  attributeChangedCallback() {
    this.syncButtons()
  }

  // Native disabled at the bounds; the click handlers keep their clamp as the net.
  syncButtons() {
    const value = parseInt(this.value)
    if (this.plusButton) this.plusButton.disabled = value >= this.max
    if (this.minusButton) this.minusButton.disabled = value <= this.min
  }

  get min() {
    const min = parseInt(this.getAttribute('min'))
    return Number.isNaN(min) ? 1 : min
  }

  get max() {
    // NaN-safe (|| would turn a legit max="0" into Infinity)
    const max = parseInt(this.getAttribute('max'))
    return Number.isNaN(max) ? Infinity : max
  }

  setupEventListeners() {
    this.plusButton?.addEventListener('click', () => {
      const currentValue = parseInt(this.value)
      const newValue = Math.min(isNaN(currentValue) ? 0 : currentValue + 1, this.max)
      if (newValue !== parseInt(this.value)) {
        this.value = newValue
        this.input.value = newValue
        this.publishQtyChange()
      }
    })

    this.minusButton?.addEventListener('click', () => {
      const currentValue = parseInt(this.value)
      const newValue = Math.max(isNaN(currentValue) ? 0 : currentValue - 1, this.min)
      if (newValue !== parseInt(this.value)) {
        this.value = newValue
        this.input.value = newValue
        this.publishQtyChange()
      }
    })

    // Typed values are validated, not silently clamped: a number that snaps to
    // something else with no explanation reads as a glitch. Out of bounds gets the
    // browser's native validity bubble naming the limit, and the field returns to
    // the last committed value; nothing is published. The +/- buttons need none of
    // this, they cannot leave the bounds. Messages can be overridden per instance
    // via data-min-error / data-max-error ("[min]"/"[max]" are substituted).
    this.input?.addEventListener('change', (event) => {
      this.input.setCustomValidity('')
      const newValue = parseInt(event.target.value)
      let message = ''
      if (isNaN(newValue)) {
        message = this.dataset.numberError || 'Enter a number'
      } else if (newValue < this.min) {
        message = (this.dataset.minError || 'Minimum quantity is [min]').replace('[min]', this.min)
      } else if (newValue > this.max) {
        message = (this.dataset.maxError || 'Only [max] available').replace('[max]', this.max)
      }

      if (message) {
        this.input.setCustomValidity(message)
        this.input.reportValidity()
        this.input.value = this.value
        this.input.select?.()
        return
      }

      this.value = newValue
      this.input.value = newValue
      this.publishQtyChange()
    })

    this.input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.keyCode === 13) {
        event.preventDefault()
        event.target.blur()
      }
    })
  }

  // Silent reset back to min — callers own any follow-up recalculation.
  reset() {
    this.value = this.min
    if (this.input) this.input.value = this.min
  }

  publishQtyChange() {
    publish(EVENTS.qtyChange, {
      detail: {
        value: this.value,
        name: this.name,
        id: this.dataset.id,
      }
    })
  }

  get value() {
    const attrValue = this.getAttribute('value')
    if (attrValue !== null) return attrValue
    if (this.input?.value !== undefined) return this.input.value
    return String(this.min)
  }

  set value(val) {
    this.setAttribute('value', val)
  }

  get name() {
    return this.input?.name || ''
  }

}

customElements.define('ia-product-qty', IaQtyPicker) 